import React, { FC, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, Text } from '@chakra-ui/react';

const F = { ui:"'Rajdhani','Arial',sans-serif", mono:"'Share Tech Mono',monospace" };
const C = { card:'#131720', border:'#1E2330', gold:'#D4A017', teal:'#19E6CF',
  white:'#F0F4FF', dim:'#6B7590' };

interface TierGateProps {
  allowed: boolean;
  feature: string;
  requiredTier: 'starter' | 'pro' | 'pctds';
  children: ReactNode;
  inline?: boolean; // show inline lock instead of full page
}

const TIER_LABELS = {
  starter: 'TD Starter',
  pro: 'TD Pro',
  pctds: 'PCTDS Sanctioned',
};

const TIER_PRICES = {
  starter: '$29/mo',
  pro: '$79/mo',
  pctds: '$149/mo',
};

const TIER_COLORS = {
  starter: '#3B82F6',
  pro: C.teal,
  pctds: C.gold,
};

export const TierGate: FC<TierGateProps> = ({
  allowed, feature, requiredTier, children, inline = false
}) => {
  const navigate = useNavigate();

  if (allowed) return <>{children}</>;

  if (inline) {
    return (
      <Box bg={C.card} border={`1px solid ${C.border}`} borderRadius={10}
        p={4} textAlign="center" position="relative" overflow="hidden">
        {/* Blurred overlay */}
        <Box position="absolute" inset={0} bg="rgba(8,10,14,0.85)"
          display="flex" flexDirection="column" alignItems="center"
          justifyContent="center" zIndex={1} px={4}>
          <Text style={{ fontSize:28, marginBottom:8 }}>🔒</Text>
          <Text style={{ fontFamily:F.ui, fontSize:15, fontWeight:700,
            color:C.white, marginBottom:4 }}>{feature}</Text>
          <Text style={{ fontFamily:F.mono, fontSize:10, marginBottom:12,
            color:TIER_COLORS[requiredTier], letterSpacing:'0.15em' }}>
            {TIER_LABELS[requiredTier].toUpperCase()} · {TIER_PRICES[requiredTier]}
          </Text>
          <Box as="button" px={5} py={2}
            bg="transparent"
            border={`1px solid ${TIER_COLORS[requiredTier]}`}
            borderRadius={6} fontSize="13px" fontWeight="700"
            color={TIER_COLORS[requiredTier]}
            fontFamily={F.ui} cursor="pointer"
            onClick={() => navigate('/pricing')}>
            Upgrade to unlock →
          </Box>
        </Box>
        {/* Ghost content behind */}
        <Box opacity={0.1} pointerEvents="none">{children}</Box>
      </Box>
    );
  }

  return (
    <Box maxW="560px" mx="auto" px={4} py={16} textAlign="center"
      fontFamily={F.ui}>
      <Text style={{ fontSize:48, marginBottom:16 }}>🔒</Text>
      <Text style={{ fontFamily:F.ui, color:C.white, fontSize:28,
        fontWeight:900, marginBottom:8 }}>
        {feature}
      </Text>
      <Text style={{ fontFamily:F.ui, fontSize:15, color:C.dim,
        lineHeight:1.7, marginBottom:8 }}>
        This feature requires a{' '}
        <Box as="span" style={{ color:TIER_COLORS[requiredTier], fontWeight:700 }}>
          {TIER_LABELS[requiredTier]}
        </Box>{' '}
        plan or higher.
      </Text>
      <Box display="inline-flex" alignItems="center" gap={2}
        bg={`${TIER_COLORS[requiredTier]}18`}
        border={`1px solid ${TIER_COLORS[requiredTier]}44`}
        borderRadius={20} px={4} py={2} mb={8}>
        <Text style={{ fontFamily:F.mono, fontSize:12,
          color:TIER_COLORS[requiredTier], letterSpacing:'0.15em' }}>
          {TIER_LABELS[requiredTier].toUpperCase()} · {TIER_PRICES[requiredTier]}
        </Text>
      </Box>
      <Flex gap={3} justify="center" flexWrap="wrap">
        <Box as="button" px={8} py={3}
          style={{ background:`linear-gradient(135deg, ${TIER_COLORS[requiredTier]}, ${TIER_COLORS[requiredTier]}BB)`,
            border:'none', borderRadius:8, fontSize:15, fontWeight:800,
            color: requiredTier === 'pctds' ? '#000' : '#000',
            fontFamily:F.ui, cursor:'pointer' }}
          onClick={() => navigate('/pricing')}>
          See Pricing & Upgrade →
        </Box>
        <Box as="button" px={6} py={3}
          bg="transparent" border={`1px solid ${C.border}`}
          borderRadius={8} fontSize={14} color={C.dim}
          fontFamily={F.ui} cursor="pointer"
          onClick={() => window.history.back()}>
          ← Go Back
        </Box>
      </Flex>
      <Text style={{ fontFamily:F.ui, fontSize:12, color:C.dim,
        marginTop:16 }}>
        Your 30-day free trial includes full Pro access.
        Start your trial at pokercity.com/become-td.
      </Text>
    </Box>
  );
};

export default TierGate;
